import React, { useState, useEffect } from "react";
import {
    Box,
    Card,
    Typography,
    Button,
    TextField,
    CircularProgress,
    Divider,
    IconButton,
    Tooltip,
    Select,
    MenuItem,
    FormControl,
    Chip,
} from "@mui/material";
import {
    CloudUpload,
    CloudDownload,
    Apple,
    Computer,
    Delete,
    History,
    Download,
    ContentCopy,
    Check,
} from "@mui/icons-material";
import {
    uploadAppFile,
    getLatestAppFiles,
    deleteAppFile,
    getAllVersionsByPlatform,
} from "../../../services/appDownload.service";
import { ToastError, ToastSuccess } from "../../../utils/Toaster/Toaster";
import Styles from "../Dashboard.module.scss";

// Stable, public endpoint that always 302-redirects to the latest Linux .deb on
// S3. Linux users install from the terminal instead of using a download button.
const LINUX_INSTALL_URL =
    "https://api.lamynaals.com/api/lmt/downloadLatestLinux";
const LINUX_INSTALL_COMMANDS = `wget -O time-logger.deb ${LINUX_INSTALL_URL}\nsudo apt install ./time-logger.deb`;

const AppDownloadCard = ({ isAdmin = false }) => {
    const [appFiles, setAppFiles] = useState({
        Mac: null,
        Linux: null,
        Windows: null,
    });
    const [allVersions, setAllVersions] = useState({
        Mac: [],
        Linux: [],
        Windows: [],
    });
    const [selectedVersions, setSelectedVersions] = useState({
        Mac: null,
        Linux: null,
        Windows: null,
    });
    const [loading, setLoading] = useState(false);
    const [uploadLoading, setUploadLoading] = useState({
        Mac: false,
        Linux: false,
        Windows: false,
    });
    const [versions, setVersions] = useState({
        Mac: "",
        Linux: "",
        Windows: "",
    });
    const [selectedFiles, setSelectedFiles] = useState({
        Mac: null,
        Linux: null,
        Windows: null,
    });
    const [copiedLinux, setCopiedLinux] = useState(false);

    useEffect(() => {
        fetchLatestFiles();
        // Fetch all versions for both admin and employees
        fetchAllVersions("Mac");
        fetchAllVersions("Linux");
        fetchAllVersions("Windows");
    }, [isAdmin]);

    const fetchLatestFiles = async () => {
        setLoading(true);
        try {
            const response = await getLatestAppFiles();
            if (response.data && response.data.data) {
                setAppFiles(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching app files:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllVersions = async (platform) => {
        try {
            const response = await getAllVersionsByPlatform(platform);
            if (response.data && !response.data.error) {
                setAllVersions((prev) => ({
                    ...prev,
                    [platform]: response.data.data,
                }));
                // Set the latest version as default
                if (response.data.data.length > 0) {
                    setSelectedVersions((prev) => ({
                        ...prev,
                        [platform]: response.data.data[0],
                    }));
                }
            }
        } catch (error) {
            console.error(`Error fetching ${platform} versions:`, error);
        }
    };

    const handleFileSelect = (platform, event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFiles((prev) => ({ ...prev, [platform]: file }));
        }
    };

    const handleVersionChange = (platform, value) => {
        setVersions((prev) => ({ ...prev, [platform]: value }));
    };

    const handleVersionSelect = (platform, versionId) => {
        const version = allVersions[platform].find((v) => v._id === versionId);
        setSelectedVersions((prev) => ({ ...prev, [platform]: version }));
    };

    const handleUpload = async (platform) => {
        if (!selectedFiles[platform]) {
            ToastError("Please select a file");
            return;
        }
        if (!versions[platform]) {
            ToastError("Please enter a version");
            return;
        }

        // Check for duplicate version
        const isDuplicate = allVersions[platform]?.some(
            (v) => v.version === versions[platform]
        );

        if (isDuplicate) {
            ToastError(`Version ${versions[platform]} already exists for ${platform}`);
            return;
        }

        setUploadLoading((prev) => ({ ...prev, [platform]: true }));
        try {
            const formData = new FormData();
            const bodyData = {
                platform,
                version: versions[platform],
            };
            formData.append("bodyData", JSON.stringify(bodyData));
            formData.append("file", selectedFiles[platform]);

            const response = await uploadAppFile(formData);
            if (response.data && !response.data.error) {
                ToastSuccess(`${platform} app uploaded successfully`);
                setSelectedFiles((prev) => ({ ...prev, [platform]: null }));
                setVersions((prev) => ({ ...prev, [platform]: "" }));
                fetchLatestFiles();
                if (isAdmin) {
                    fetchAllVersions(platform);
                }
            } else {
                ToastError(response.data.message || "Upload failed");
            }
        } catch (error) {
            ToastError(error.response?.data?.message || "Upload failed");
        } finally {
            setUploadLoading((prev) => ({ ...prev, [platform]: false }));
        }
    };

    const handleDownload = (platform, specificFile = null) => {
        const file = specificFile || (isAdmin ? appFiles[platform] : selectedVersions[platform]);

        if (file && file.file && file.file.location) {
            window.open(file.file.location, "_blank");
        } else {
            ToastError("No file available for download");
        }
    };

    const handleCopyLinuxCommands = async () => {
        try {
            await navigator.clipboard.writeText(LINUX_INSTALL_COMMANDS);
            setCopiedLinux(true);
            ToastSuccess("Commands copied to clipboard");
            setTimeout(() => setCopiedLinux(false), 2000);
        } catch (error) {
            ToastError("Could not copy commands");
        }
    };

    const handleDelete = async (platform, versionId) => {
        if (!versionId) return;

        try {
            const response = await deleteAppFile(versionId);
            if (response.data && !response.data.error) {
                ToastSuccess(`Version deleted successfully`);
                fetchLatestFiles();
                if (isAdmin) {
                    fetchAllVersions(platform);
                }
                // Clear selection
                setSelectedVersions((prev) => ({ ...prev, [platform]: null }));
            }
        } catch (error) {
            ToastError("Delete failed");
        }
    };

    const getPlatformIcon = (platform) => {
        switch (platform) {
            case "Mac":
                return <Apple sx={{ fontSize: 32, color: "#008080" }} />;
            case "Linux":
                return <Computer sx={{ fontSize: 32, color: "#008080" }} />;
            case "Windows":
                return <Computer sx={{ fontSize: 32, color: "#008080" }} />;
            default:
                return null;
        }
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const renderPlatformSection = (platform) => {
        const file = appFiles[platform];
        const hasFile = file && file.file;
        const platformVersions = allVersions[platform] || [];
        const selectedVersion = selectedVersions[platform];

        return (
            <Box key={platform} className={Styles.platformBox}>
                <Box className={Styles.platformHeader}>
                    <Box className={Styles.platformInfo}>
                        {getPlatformIcon(platform)}
                        <Box>
                            <Typography className={Styles.platformName}>
                                {platform}
                            </Typography>
                            {isAdmin && hasFile && (
                                <Typography className={Styles.platformVersion}>
                                    Latest: v{file.version}
                                </Typography>
                            )}
                            {!isAdmin && platformVersions.length > 0 && (
                                <Chip
                                    label={`${platformVersions.length} version${platformVersions.length > 1 ? "s" : ""}`}
                                    size="small"
                                    sx={{
                                        bgcolor: "#E0F2F1",
                                        color: "#008080",
                                        fontSize: "11px",
                                        height: "20px",
                                        mt: 0.5,
                                    }}
                                />
                            )}
                        </Box>
                    </Box>

                </Box>

                {isAdmin ? (
                    <Box className={Styles.uploadSection}>
                        {platformVersions.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                                    <History sx={{ fontSize: 16, color: "#71717A" }} />
                                    <Typography sx={{ fontSize: "12px", color: "#71717A", fontWeight: 600 }}>
                                        Manage Versions ({platformVersions.length})
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                    <FormControl size="small" sx={{ flex: 1 }}>
                                        <Select
                                            value={selectedVersions[platform]?._id || ""}
                                            onChange={(e) => handleVersionSelect(platform, e.target.value)}
                                            displayEmpty
                                            sx={{
                                                fontSize: "13px",
                                                bgcolor: "#F9FAFB",
                                                "& .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: "#E4E4E7",
                                                },
                                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: "#008080",
                                                },
                                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: "#008080",
                                                },
                                            }}
                                        >
                                            <MenuItem value="" disabled>
                                                <Typography sx={{ fontSize: "13px", color: "#A1A1AA" }}>
                                                    Select version to delete
                                                </Typography>
                                            </MenuItem>
                                            {platformVersions.map((version) => (
                                                <MenuItem key={version._id} value={version._id}>
                                                    <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                                                        <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                                                            v{version.version}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: "11px", color: "#71717A", ml: 1 }}>
                                                            {formatDate(version.uploadedAt)}
                                                        </Typography>
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Tooltip title="Download selected version">
                                        <Box sx={{ flexShrink: 0 }}>
                                            <Button
                                                variant="contained"
                                                onClick={() => handleDownload(platform, selectedVersions[platform])}
                                                disabled={!selectedVersions[platform]}
                                                sx={{
                                                    minWidth: "40px",
                                                    width: "40px",
                                                    height: "40px",
                                                    p: 0,
                                                    bgcolor: "#008080",
                                                    "&:hover": { bgcolor: "#006666" },
                                                    "&.Mui-disabled": { bgcolor: "#F4F4F5", color: "#A1A1AA", border: "1px solid #E4E4E7" }
                                                }}
                                            >
                                                <Download fontSize="small" />
                                            </Button>
                                        </Box>
                                    </Tooltip>
                                    <Tooltip title="Delete selected version">
                                        <Box sx={{ flexShrink: 0 }}>
                                            <Button
                                                variant="contained"
                                                onClick={() => handleDelete(platform, selectedVersions[platform]?._id)}
                                                disabled={!selectedVersions[platform]}
                                                sx={{
                                                    minWidth: "40px",
                                                    width: "40px",
                                                    height: "40px",
                                                    p: 0,
                                                    bgcolor: "#008080",
                                                    "&:hover": { bgcolor: "#006666" },
                                                    "&.Mui-disabled": { bgcolor: "#F4F4F5", color: "#A1A1AA", border: "1px solid #E4E4E7" }
                                                }}
                                            >
                                                <Delete fontSize="small" />
                                            </Button>
                                        </Box>
                                    </Tooltip>
                                </Box>
                            </Box>
                        )}
                        <TextField
                            size="small"
                            placeholder="Version (e.g., 1.0.0)"
                            value={versions[platform]}
                            onChange={(e) => handleVersionChange(platform, e.target.value)}
                            fullWidth
                            className={Styles.versionInput}
                        />
                        <Box className={Styles.buttonGroup}>
                            <Button
                                variant="outlined"
                                component="label"
                                size="small"
                                fullWidth
                                sx={{
                                    textTransform: "capitalize",
                                    borderColor: "#008080",
                                    color: "#008080",
                                    fontSize: "12px",
                                    "&:hover": { borderColor: "#055253", bgcolor: "#F0FAFA" },
                                }}
                            >
                                Choose
                                <input
                                    type="file"
                                    hidden
                                    onChange={(e) => handleFileSelect(platform, e)}
                                />
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                fullWidth
                                onClick={() => handleUpload(platform)}
                                disabled={uploadLoading[platform]}
                                startIcon={
                                    uploadLoading[platform] ? (
                                        <CircularProgress size={14} color="inherit" />
                                    ) : (
                                        <CloudUpload fontSize="small" />
                                    )
                                }
                                sx={{
                                    textTransform: "capitalize",
                                    bgcolor: "#008080",
                                    fontSize: "12px",
                                    "&:hover": { bgcolor: "#055253" },
                                }}
                            >
                                Upload
                            </Button>
                        </Box>
                        {selectedFiles[platform] && (
                            <Typography className={Styles.fileName}>
                                {selectedFiles[platform].name}
                            </Typography>
                        )}
                    </Box>
                ) : platform === "Linux" ? (
                    <Box className={Styles.downloadSection}>
                        {platformVersions.length > 0 ? (
                            <>
                                <Typography sx={{ fontSize: "12px", color: "#71717A", mb: 1 }}>
                                    Open a terminal and run:
                                </Typography>
                                <Box
                                    sx={{
                                        position: "relative",
                                        bgcolor: "#1E1E1E",
                                        color: "#E4E4E7",
                                        borderRadius: "6px",
                                        p: 1.5,
                                        pr: 5,
                                        fontFamily: "monospace",
                                        fontSize: "12px",
                                        lineHeight: 1.7,
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-all",
                                    }}
                                >
                                    <Box component="span" sx={{ color: "#4ADE80" }}>$ </Box>
                                    wget -O time-logger.deb {LINUX_INSTALL_URL}{"\n"}
                                    <Box component="span" sx={{ color: "#4ADE80" }}>$ </Box>
                                    sudo apt install ./time-logger.deb
                                    <Tooltip title={copiedLinux ? "Copied!" : "Copy"}>
                                        <IconButton
                                            onClick={handleCopyLinuxCommands}
                                            size="small"
                                            sx={{
                                                position: "absolute",
                                                top: 6,
                                                right: 6,
                                                color: copiedLinux ? "#4ADE80" : "#A1A1AA",
                                                "&:hover": { color: "#fff" },
                                            }}
                                        >
                                            {copiedLinux ? (
                                                <Check fontSize="small" />
                                            ) : (
                                                <ContentCopy fontSize="small" />
                                            )}
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                {hasFile && (
                                    <Typography sx={{ fontSize: "11px", color: "#71717A", mt: 1 }}>
                                        Latest: v{file.version} — installs the newest build automatically
                                    </Typography>
                                )}
                            </>
                        ) : (
                            <Typography sx={{ fontSize: "12px", color: "#71717A", textAlign: "center", py: 2 }}>
                                No versions available
                            </Typography>
                        )}
                    </Box>
                ) : (
                    <Box className={Styles.downloadSection}>
                        {platformVersions.length > 0 ? (
                            <>
                                <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                                    <Select
                                        value={selectedVersion?._id || ""}
                                        onChange={(e) => handleVersionSelect(platform, e.target.value)}
                                        displayEmpty
                                        sx={{
                                            fontSize: "13px",
                                            "& .MuiOutlinedInput-notchedOutline": {
                                                borderColor: "#E4E4E7",
                                            },
                                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                                borderColor: "#008080",
                                            },
                                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                borderColor: "#008080",
                                            },
                                        }}
                                    >
                                        {platformVersions.map((version) => (
                                            <MenuItem key={version._id} value={version._id}>
                                                <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                                                    <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                                                        v{version.version}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: "11px", color: "#71717A", ml: 2 }}>
                                                        {formatDate(version.uploadedAt)}
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {selectedVersion && (
                                    <Box sx={{ mb: 1.5, p: 1, bgcolor: "#F9FAFB", borderRadius: "6px" }}>
                                        <Typography sx={{ fontSize: "11px", color: "#71717A", mb: 0.5 }}>
                                            Uploaded by: {selectedVersion.uploadedBy?.firstName} {selectedVersion.uploadedBy?.lastName}
                                        </Typography>
                                        <Typography sx={{ fontSize: "11px", color: "#71717A" }}>
                                            Date: {formatDate(selectedVersion.uploadedAt)}
                                        </Typography>
                                    </Box>
                                )}
                                <Button
                                    variant="contained"
                                    size="small"
                                    fullWidth
                                    onClick={() => handleDownload(platform)}
                                    disabled={!selectedVersion}
                                    startIcon={<CloudDownload />}
                                    sx={{
                                        textTransform: "capitalize",
                                        bgcolor: "#008080",
                                        "&:hover": { bgcolor: "#055253" },
                                        "&:disabled": { bgcolor: "#E4E4E7", color: "#A1A1AA" },
                                    }}
                                >
                                    Download v{selectedVersion?.version}
                                </Button>
                            </>
                        ) : (
                            <Typography sx={{ fontSize: "12px", color: "#71717A", textAlign: "center", py: 2 }}>
                                No versions available
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        );
    };

    if (loading) {
        return (
            <Card sx={{ p: 2, borderRadius: "10px", textAlign: "center" }}>
                <CircularProgress size={24} sx={{ color: "#008080" }} />
            </Card>
        );
    }

    return (
        <Card className={Styles.appDownloadCard}>
            <Box className={Styles.Item_1_Heading}>
                <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>
                    {isAdmin ? "Upload Applications" : "Download Applications"}
                </Typography>
            </Box>
            <Divider sx={{ mb: 2, opacity: 1, color: "#E4E4E7" }} />
            <Box className={Styles.platformContainer}>
                {renderPlatformSection("Mac")}
                {renderPlatformSection("Linux")}
                {renderPlatformSection("Windows")}
            </Box>
        </Card>
    );
};

export default AppDownloadCard;
